import React, { useState } from 'react';
import {
  X, Leaf, Clock, Star, MapPinned,
  Sparkles, Utensils, PartyPopper, SlidersHorizontal,
} from 'lucide-react';

import {
  AMBIENCE_FILTERS, VIBE_FILTERS, CUISINE_CATS, OCCASION_FILTERS,
  RATING_OPTIONS, DISTANCE_OPTIONS,
} from './filterData.js';
import './style/FilterSheet.css';

const TABS = [
  { key: 'quick',    label: 'Basics',   icon: SlidersHorizontal },
  { key: 'vibes',    label: 'Vibes & Themes', icon: Sparkles },
  { key: 'cuisine',  label: 'Cuisine',  icon: Utensils },
  { key: 'occasion', label: 'Occasion', icon: PartyPopper },
];

const FilterSheet = ({
  open, onClose,
  vegMode, setVegMode, isOpen, setIsOpen,
  activeAmbience, toggleAmbience,
  activeVibes, toggleVibe,
  activeCuisine, toggleCuisine,
  activeOccasion, toggleOccasion,
  minRating, setMinRating,
  maxDistance, setMaxDistance,
  activeFilterCount,
  onClearAll,
}) => {
  const [tab, setTab] = useState('quick');

  if (!open) return null;

  return (
    <>
      <div className="fs-overlay" onClick={onClose} />
      <div className="fs-sheet" role="dialog" aria-modal="true" aria-label="Filters">
        <div className="fs-header">
          <div className="fs-header-title">
            Filters
            {activeFilterCount > 0 && <span className="fs-count">{activeFilterCount}</span>}
          </div>
          <button className="fs-close" onClick={onClose} aria-label="Close filters"><X size={20} /></button>
        </div>

        {/* Tabs */}
        <div className="fs-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                className={`fs-tab ${tab === t.key ? 'fs-tab--active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                <Icon size={15} />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="fs-body">

          {/* ── BASICS: diet / status / rating / distance ── */}
          {tab === 'quick' && (
            <div className="fs-pane">
              <p className="fs-sheet-label">Diet &amp; Status</p>
              <div className="fs-chip-row">
                <button className={`fs-chip ${vegMode ? 'fs-chip--green' : ''}`} onClick={() => setVegMode(p => !p)}>
                  <Leaf size={14} /> Veg Only
                </button>
                <button className={`fs-chip ${isOpen ? 'fs-chip--rust' : ''}`} onClick={() => setIsOpen(p => !p)}>
                  <Clock size={14} /> Open Now
                </button>
              </div>

              <p className="fs-sheet-label">Minimum Rating</p>
              <div className="fs-chip-row">
                {RATING_OPTIONS.map(r => (
                  <button
                    key={r.value}
                    className={`fs-chip ${minRating === r.value ? 'fs-chip--amber' : ''}`}
                    onClick={() => setMinRating(minRating === r.value ? null : r.value)}
                  >
                    <Star size={14} /> {r.label.replace('⭐ ', '')}
                  </button>
                ))}
              </div>

              <p className="fs-sheet-label">Distance</p>
              <div className="fs-chip-row">
                {DISTANCE_OPTIONS.map(d => (
                  <button
                    key={d.label}
                    className={`fs-chip ${maxDistance === d.value ? 'fs-chip--rust' : ''}`}
                    onClick={() => setMaxDistance(d.value)}
                  >
                    <MapPinned size={14} /> {d.label.replace('📍 ', '')}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── VIBES & THEMES: ambience + cultural, merged into one tab ── */}
          {tab === 'vibes' && (
            <div className="fs-pane">
              <p className="fs-sheet-label">☁️ Ambience</p>
              <div className="fs-chip-row fs-chip-row--wrap">
                {AMBIENCE_FILTERS.map(af => (
                  <button
                    key={af.val}
                    className={`fs-chip ${activeAmbience.includes(af.val) ? 'fs-chip--amber' : ''}`}
                    onClick={() => toggleAmbience(af.val)}
                  >
                    {af.emoji} {af.label}
                  </button>
                ))}
              </div>

              <p className="fs-sheet-label">🌍 Cultural Theme</p>
              <div className="fs-chip-row fs-chip-row--wrap">
                {VIBE_FILTERS.map(vf => (
                  <button
                    key={vf.val}
                    className={`fs-chip ${activeVibes.includes(vf.val) ? 'fs-chip--violet' : ''}`}
                    onClick={() => toggleVibe(vf.val)}
                  >
                    {vf.emoji} {vf.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── CUISINE ── */}
          {tab === 'cuisine' && (
            <div className="fs-pane">
              <p className="fs-pane-hint">What are you craving today?</p>
              <div className="fs-chip-row fs-chip-row--wrap">
                {CUISINE_CATS.map(c => (
                  <button
                    key={c.val}
                    className={`fs-chip ${activeCuisine.includes(c.val) ? 'fs-chip--rust' : ''}`}
                    onClick={() => toggleCuisine(c.val)}
                  >
                    {c.emoji} {c.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── OCCASION ── */}
          {tab === 'occasion' && (
            <div className="fs-pane">
              <p className="fs-pane-hint">What's the occasion?</p>
              <div className="fs-chip-row fs-chip-row--wrap">
                {OCCASION_FILTERS.map(o => (
                  <button
                    key={o.val}
                    className={`fs-chip ${activeOccasion.includes(o.val) ? 'fs-chip--violet' : ''}`}
                    onClick={() => toggleOccasion(o.val)}
                  >
                    {o.emoji} {o.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="fs-footer">
          <button className="fs-clear" onClick={onClearAll} disabled={activeFilterCount === 0}>
            Clear all
          </button>
          <button className="fs-apply" onClick={onClose}>
            Show results{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>
        </div>
      </div>
    </>
  );
};

export default FilterSheet;